import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import { IAutoLoginUseCase } from '../../core/usecases/auth/AutoLoginUseCase';
import { IDevicePairingUseCase } from '../../core/usecases/auth/DevicePairingUseCase';
import { IForgotPasswordResendOTPUseCase } from '../../core/usecases/auth/ForgotPasswordResendOTPUseCase';
import { IForgotPasswordResetUseCase } from '../../core/usecases/auth/ForgotPasswordResetUseCase';
import { IForgotPasswordSendOTPUseCase } from '../../core/usecases/auth/ForgotPasswordSendOTPUseCase';
import { IForgotPasswordVerifyOTPUseCase } from '../../core/usecases/auth/ForgotPasswordVerifyOTPUseCase';
import { ILoginWithCredentialsUseCase } from '../../core/usecases/auth/LoginWithCredentialsUseCase';
import { ILoginWithOTPUseCase } from '../../core/usecases/auth/LoginWithOTPUseCase';
import { IRefreshAuthTokenUseCase } from '../../core/usecases/auth/RefreshAuthTokenUseCase';
import { IVerifyQRCodeUseCase } from '../../core/usecases/auth/VerifyQRCodeUseCase';
import { IVerifyUsernameUseCase } from '../../core/usecases/auth/VerifyUsernameUseCase';
import { IZendeskJWTTokenUseCase } from '../../core/usecases/auth/ZendeskJWTTokenUseCase';
import { IGetGuestPublicConfigUseCase } from '../../core/usecases/auth/GetGuestPublicConfigUseCase';
import { ILoginGuestUseCase } from '../../core/usecases/auth/LoginGuestUseCase';
import { ILocateUserEntity, IUserDetailsEntity } from '../../entity';
import { STORE_NAME } from '@intigral/constant';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import {
    IAuthApisRefreshAuthTokenResponse,
    IZendeskJWTTokenPayload,
    IZendeskJWTTokenResponse,
    IDevicePairRequest,
    IDevicePairResponse,
    IApisVerifyUsernameResponse,
    IForgotPasswordSendOTPResponse,
    IForgotPasswordVerifyOTPResponse,
    IForgotPasswordResetResponse,
    IVerifyUser,
    IForgetResendPinRequest,
    IForgetResendPinResponse,
    IForgetPinResponse,
    IAuthApisFormSigatureResponse,
    IVerifyEmailRequest,
    IVerifyEmailResponse
} from '../../entity/interface/iAuthApis';
import { IFormSignatureObjectUseCase } from '../../core/usecases/auth/FormSignatureObjectUseCase';
import { IGetSubscriptionStatusUseCase } from '../../core/usecases/subscription/GetSubscriptionStatusUseCase';
import { IVerifyUserUseCase } from '../../core/usecases/auth/VerifyUserUseCase';
import { ILogoutUseCase } from '../../core/usecases/auth/LogoutUseCase';
import { IForgetResendPinUseCase } from '../../core/usecases/auth/ForgetResendPinUseCase';
import { IFormSignatureUseCase } from '../../core/usecases/auth/FormSignatureUseCase';
import { IVerifyEmailUseCase } from '../../core/usecases/auth/VerifyEmailUseCase';
import { ILocateUsernameUseCase } from '../../core/usecases/auth/LocateUsernameUseCase';
import { IB2BForgetPinUseCase } from '../../core/usecases/auth/B2BForgetPinUseCase';

export interface IAuthService {
    getFormSignature(): Promise<{ tokenName: string; token: string } | null>;
    refreshAuthToken(
        username: string,
        authToken: string,
        identityToken: string,
        authSign: string
    ): Promise<IAuthApisRefreshAuthTokenResponse['data']>;
    zendeskJWTToken(
        payload: IZendeskJWTTokenPayload,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IZendeskJWTTokenResponse['data']>;
    devicePairing(payload: IDevicePairRequest, apiKey?: { [key: string]: string }): Promise<IDevicePairResponse>;
    verifyUsername(
        username: string,
        lang: string,
        signature: string,
        signatureKey?: string,
        recaptchaToken?: string | null
    ): Promise<IApisVerifyUsernameResponse['data']>;

    locateUser(
        username: string,
        lang: string
    ): Promise<ILocateUserEntity['data']>;
    loginWithCredentials(
        username: string,
        password: string,
        signature: string,
        signatureKey?: string,
        recaptchaToken?: string | null
    ): Promise<IUserDetailsEntity>;
    loginWithOTP(
        username: string,
        otp: string,
        lang: string,
        signature: string,
        signatureKey?: string,
        recaptchaToken?: string | null
    ): Promise<IUserDetailsEntity>;
    getGuestPublicConfig(url: string): Promise<unknown | null>;
    loginGuest(url: string, payload: { username: string; envKey: string; client: string }): Promise<unknown | null>;
    forgotPasswordSendOTP(
        username: string,
        lang: string,
        signature: string,
        signatureKey?: string,
        recaptchaToken?: string | null
    ): Promise<IForgotPasswordSendOTPResponse>;
    forgotPasswordResendOTP(
        username: string,
        lang: string,
        signature: string,
        signatureKey?: string,
        recaptchaToken?: string | null
    ): Promise<IForgotPasswordSendOTPResponse>;
    forgotPasswordVerifyOTP(
        username: string,
        pinValue: string,
        verifyGuid: string,
        signature: string,
        signatureKey?: string,
        recaptchaToken?: string | null
    ): Promise<IForgotPasswordVerifyOTPResponse>;
    forgotPasswordReset(
        username: string,
        pinValue: string,
        verifyGuid: string,
        password: string,
        signature: string,
        signatureKey?: string,
        recaptchaToken?: string | null
    ): Promise<IForgotPasswordResetResponse>;
    verifyQRCode(
        qrCode: string,
        make: string,
        model: string,
        platform: string,
        lang: string,
        signature?: string,
        signatureKey?: string
    ): Promise<IUserDetailsEntity>;
    verifyUser(username: string, recaptchaToken: string | null): Promise<IVerifyUser>;
    autoLogin(
        username: string,
        identityToken: string,
        authSign: string,
        userLoginToken: string,
        recaptchaToken: string | null
    ): Promise<any>;
    logout(username: string, authToken: string, identityToken: string, authSign: string): Promise<boolean>;
    forgetResendPin(payload: IForgetResendPinRequest): Promise<IForgetResendPinResponse>;
    b2bForgotPin(apiKey: string, username: string, identityToken: string, authToken: string): Promise<IForgetPinResponse['data']>;

    verifyEmail(payload: IVerifyEmailRequest): Promise<IVerifyEmailResponse>;
}
export class AuthService implements IAuthService {
    private get autoLoginUseCase(): IAutoLoginUseCase {
        return container.resolve<IAutoLoginUseCase>(DI.USECASES.IAutoLoginUseCase);
    }

    private get devicePairingUseCase(): IDevicePairingUseCase {
        return container.resolve<IDevicePairingUseCase>(DI.USECASES.IDevicePairingUseCase);
    }

    private get forgotPasswordResendOTPUseCase(): IForgotPasswordResendOTPUseCase {
        return container.resolve<IForgotPasswordResendOTPUseCase>(DI.USECASES.IForgotPasswordResendOTPUseCase);
    }

    private get forgotPasswordResetUseCase(): IForgotPasswordResetUseCase {
        return container.resolve<IForgotPasswordResetUseCase>(DI.USECASES.IForgotPasswordResetUseCase);
    }

    private get forgotPasswordSendOTPUseCase(): IForgotPasswordSendOTPUseCase {
        return container.resolve<IForgotPasswordSendOTPUseCase>(DI.USECASES.IForgotPasswordSendOTPUseCase);
    }

    private get forgotPasswordVerifyOTPUseCase(): IForgotPasswordVerifyOTPUseCase {
        return container.resolve<IForgotPasswordVerifyOTPUseCase>(DI.USECASES.IForgotPasswordVerifyOTPUseCase);
    }

    private get loginWithCredentialsUseCase(): ILoginWithCredentialsUseCase {
        return container.resolve<ILoginWithCredentialsUseCase>(DI.USECASES.ILoginWithCredentialsUseCase);
    }

    private get loginWithOTPUseCase(): ILoginWithOTPUseCase {
        return container.resolve<ILoginWithOTPUseCase>(DI.USECASES.ILoginWithOTPUseCase);
    }

    private get refreshAuthTokenUseCase(): IRefreshAuthTokenUseCase {
        return container.resolve<IRefreshAuthTokenUseCase>(DI.USECASES.IRefreshAuthTokenUseCase);
    }

    private get verifyQRCodeUseCase(): IVerifyQRCodeUseCase {
        return container.resolve<IVerifyQRCodeUseCase>(DI.USECASES.IVerifyQRCodeUseCase);
    }

    private get verifyUsernameUseCase(): IVerifyUsernameUseCase {
        return container.resolve<IVerifyUsernameUseCase>(DI.USECASES.IVerifyUsernameUseCase);
    }
    private get locateUsernameUseCase(): ILocateUsernameUseCase {
        return container.resolve<ILocateUsernameUseCase>(DI.USECASES.ILocateUsernameUseCase);
    }

    private get getGuestPublicConfigUseCase(): IGetGuestPublicConfigUseCase {
        return container.resolve<IGetGuestPublicConfigUseCase>(DI.USECASES.IGetGuestPublicConfigUseCase);
    }

    private get loginGuestUseCase(): ILoginGuestUseCase {
        return container.resolve<ILoginGuestUseCase>(DI.USECASES.ILoginGuestUseCase);
    }

    private get zendeskJWTTokenUseCase(): IZendeskJWTTokenUseCase {
        return container.resolve<IZendeskJWTTokenUseCase>(DI.USECASES.IZendeskJWTTokenUseCase);
    }

    private get formSignatureObjectUseCase(): IFormSignatureObjectUseCase {
        return container.resolve<IFormSignatureObjectUseCase>(DI.USECASES.IFormSignatureObjectUseCase);
    }
    private get formSignatureUseCase(): IFormSignatureUseCase {
        return container.resolve<IFormSignatureUseCase>(DI.USECASES.IFormSignatureUseCase);
    }

    private get getSubscriptionStatusUseCase(): IGetSubscriptionStatusUseCase {
        return container.resolve<IGetSubscriptionStatusUseCase>(DI.USECASES.IGetSubscriptionStatusUseCase);
    }

    private get verifyUserUseCase(): IVerifyUserUseCase {
        return container.resolve<IVerifyUserUseCase>(DI.USECASES.IVerifyUserUseCase);
    }

    private get logoutUseCase(): ILogoutUseCase {
        return container.resolve<ILogoutUseCase>(DI.USECASES.ILogoutUseCase);
    }

    private get forgetResendPinUseCase(): IForgetResendPinUseCase {
        return container.resolve<IForgetResendPinUseCase>(DI.USECASES.IForgetResendPinUseCase);
    }
    private get verifyEmailUseCase(): IVerifyEmailUseCase {
        return container.resolve<IVerifyEmailUseCase>(DI.USECASES.IVerifyEmailUseCase);
    }

    private get b2bForgetPinUseCase(): IB2BForgetPinUseCase {
        return container.resolve<IB2BForgetPinUseCase>(DI.USECASES.IB2BForgetPinUseCase);
    }


    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    /**
     * @typedef {Object} CsrfToken
     * @property {string} token - The CSRF token value.
     * @property {string} tokenName - The name of the CSRF token cookie.
     */
    async getFormSignature(): Promise<IAuthApisFormSigatureResponse['data']> {
        const data = await this.formSignatureUseCase.execute();
        return data || null;
    }

    /**
     * Refreshes the auth token using the provided credentials.
     * @param {string} username - The username to authenticate with.
     * @param {string} authToken - The current auth token to refresh.
     * @param {string} identityToken - The identity token.
     * @param {string} authSign - The auth signature.
     * @returns {Promise<IAuthApisRefreshAuthTokenResponse['data']>} A promise resolving to the refreshed auth token response data.
     * @throws {Error} If the API token is invalid.
     */
    async refreshAuthToken(
        username: string,
        authToken: string,
        identityToken: string,
        authSign: string
    ): Promise<IAuthApisRefreshAuthTokenResponse['data']> {
        const storedUser = get(STORE_NAME.USER) as { isGuest?: boolean } | null | undefined;
        if (storedUser?.isGuest) {
            throw new Error('Auth token refresh is not allowed for guest browse sessions');
        }

        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.refreshAuthTokenUseCase.execute(username, authToken, identityToken, authSign, apiToken);
    }

    /**
     * Gets a JWT token for Zendesk API authentication.
     * @param {object} payload - The payload for the Zendesk JWT token request.
     * @param {string} authToken - The auth token to use for authentication.
     * @param {string} authSign - The auth signature to use for authentication.
     * @returns {Promise<IZendeskJWTTokenResponse['data']>} A promise resolving to the Zendesk JWT token response data.
     */

    async zendeskJWTToken(
        payload: IZendeskJWTTokenPayload,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IZendeskJWTTokenResponse['data']> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.zendeskJWTTokenUseCase.execute(payload, authToken, authSign, identityToken, apiToken);
    }

    /**
     * Initiates device pairing by calling the auth use case with the provided payload.
     * @param {object} payload - The request payload for device pairing.
     * @returns {Promise<IDevicePairResponse>} A promise resolving to the device pairing response.
     * @throws Error if the API token is invalid.
     */
    async devicePairing(payload: IDevicePairRequest): Promise<IDevicePairResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.devicePairingUseCase.execute(payload, apiToken);
    }

    /**
     * Verifies the username in the login form and sends an OTP for enabled users.
     *
     * @param {string} username The username entered in the login form.
     * @param {string} lang The user's preferred language code.
     * @param {string} recaptchaToken The reCAPTCHA token obtained from the user.
     * @returns {Promise<IApisVerifyUsernameResponse['data']>}
     *   A promise that resolves to the data returned from the SLP API's "verifyUsername" endpoint.
     *   The data structure can be found in the `IApisVerifyUsernameResponse` interface.
     */
    async verifyUsername(
        username: string,
        lang: string,
        recaptchaToken: string | null
    ): Promise<IApisVerifyUsernameResponse['data']> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.verifyUsernameUseCase.execute(username, lang, apiToken, recaptchaToken);
    }

    /**
     * Login with credentials
     * @param username
     * @param password
     * @param recaptchaToken Recaptcha token if enabled
     * @returns {Promise<IUserDetailsEntity>} Auth user object
     */
    async loginWithCredentials(
        username: string,
        password: string,
        recaptchaToken: string | null
    ): Promise<IUserDetailsEntity> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const user = await this.loginWithCredentialsUseCase.execute(username, password, apiToken, recaptchaToken);
        return await this.addUserWithSubscriptions(user);
    }

    /**
     * Login with OTP
     * @param username
     * @param otp
     * @param recaptchaToken Recaptcha token if enabled
     * @returns {Promise<IUserDetailsEntity>} Auth user object
     */
    async loginWithOTP(
        username: string,
        otp: string,
        lang: string,
        recaptchaToken: string | null
    ): Promise<IUserDetailsEntity> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const user = await this.loginWithOTPUseCase.execute(username, otp, lang, apiToken, recaptchaToken);
        return await this.addUserWithSubscriptions(user);
    }

    async getGuestPublicConfig(url: string): Promise<unknown | null> {
        return this.getGuestPublicConfigUseCase.execute(url);
    }

    async loginGuest(
        url: string,
        payload: { username: string; envKey: string; client: string }
    ): Promise<unknown | null> {
        return this.loginGuestUseCase.execute(url, payload);
    }

    /**
     * Forgot password send OTP
     * @param username
     * @param lang
     * @param recaptchaToken Recaptcha token if enabled
     * @returns {Promise<IForgotPasswordSendOTPResponse>}
     */
    async forgotPasswordSendOTP(
        username: string,
        lang: string,
        recaptchaToken: string | null
    ): Promise<IForgotPasswordSendOTPResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return this.forgotPasswordSendOTPUseCase.execute(username, lang, apiToken, recaptchaToken);
    }

    /**
     * Forgot password Resend OTP
     * @param username
     * @param lang
     * @param recaptchaToken Recaptcha token if enabled
     * @returns {Promise<IForgotPasswordSendOTPResponse>}
     */
    async forgotPasswordResendOTP(
        username: string,
        lang: string,
        recaptchaToken: string | null
    ): Promise<IForgotPasswordSendOTPResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return this.forgotPasswordResendOTPUseCase.execute(username, lang, apiToken, recaptchaToken);
    }

    /**
     * Forgot password Verify OTP
     * @param username
     * @param pinValue
     * @param verifyGuid
     * @param recaptchaToken Recaptcha token if enabled
     * @returns {Promise<IForgotPasswordVerifyOTPResponse>}
     */
    async forgotPasswordVerifyOTP(
        username: string,
        pinValue: string,
        verifyGuid: string,
        recaptchaToken: string | null
    ): Promise<IForgotPasswordVerifyOTPResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return this.forgotPasswordVerifyOTPUseCase.execute(username, pinValue, verifyGuid, apiToken, recaptchaToken);
    }

    /**
     * Forgot password Reset
     * @param username
     * @param pinValue
     * @param verifyGuid
     * @param password
     * @param recaptchaToken Recaptcha token if enabled
     * @returns {Promise<IForgotPasswordResetResponse>}
     */
    async forgotPasswordReset(
        username: string,
        pinValue: string,
        verifyGuid: string,
        password: string,
        recaptchaToken: string | null
    ): Promise<IForgotPasswordResetResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return this.forgotPasswordResetUseCase.execute(
            username,
            pinValue,
            verifyGuid,
            password,
            apiToken,
            recaptchaToken
        );
    }

    /**
     * Verify QR code and get user auth object in response.
     * @param qrCode
     * @param make
     * @param model
     * @param platform
     * @param lang
     * @returns {Promise<IUserDetailsEntity>} Auth user object
     */
    async verifyQRCode(
        qrCode: string,
        make: string,
        model: string,
        platform: string,
        lang: string
    ): Promise<IUserDetailsEntity> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const user = await this.verifyQRCodeUseCase.execute(qrCode, make, model, platform, lang, apiToken);
        return await this.addUserWithSubscriptions(user);
    }

    private async addUserWithSubscriptions(user: IUserDetailsEntity): Promise<IUserDetailsEntity> {
        if (!user.username || !user.apiKey || !user.authToken || !user.identityToken) {
            return user;
        }
        const subscriptions = await this.getSubscriptionStatusUseCase.execute(
            user?.apiKey,
            user?.username,
            user?.authToken,
            user?.identityToken
        );

        user.subscriptionGUID = Array.isArray(subscriptions?.subList)
            ? subscriptions?.subList?.filter((item) => item?.active === true).map((sub) => sub.subscriptionGuid)
            : [];
        user.subscriptionRev = subscriptions?.subRev;
        user.subscriptionStatus = subscriptions?.subList;
        user.tierConfig = subscriptions?.tier;
        user.tierStatus = subscriptions?.tierStatus;
        user.netflixSubStatus = subscriptions?.netflixSubStatus || null;
        user.disneySubStatus = subscriptions?.disneySubStatus;
        user.amazonSubStatus = subscriptions?.amazonSubStatus;
        user.tvodList = subscriptions?.tvodList;
        user.identityToken = subscriptions?.identityToken;
        return user;
    }

    /**
     *  Verify whether the username is already in use.
     * @param username
     * @param recaptchaToken
     * @returns
     */
    async verifyUser(username: string, recaptchaToken: string | null): Promise<IVerifyUser> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return this.verifyUserUseCase.execute(username, recaptchaToken, apiToken);
    }

    /**
    *  Verify whether the username is already in use.
    * @param username
    * @param recaptchaToken
    * @returns
    */
    async locateUser(username: string, lang: string): Promise<ILocateUserEntity['data']> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return this.locateUsernameUseCase.execute(username, lang, apiToken);
    }



    /* Auto login user with username and Identity Token.
     * @param {string} username - The username to authenticate with.
     * @param {string} identityToken - The identity token.
     * @param {string} authSign - The auth signature.
     * @returns {Promise<IAuthApisRefreshAuthTokenResponse['data']} A promise resolving to the refreshed auth token response data.
     * @throws {Error} If the API token is invalid.
     */
    async autoLogin(
        username: string,
        identityToken: string,
        authSign = '',
        userLoginToken = '',
        recaptchaToken: string | null
    ): Promise<any> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.autoLoginUseCase.execute(
            username,
            identityToken,
            authSign,
            userLoginToken,
            apiToken,
            recaptchaToken
        );
    }

    /* logout API.
     * @param {string} username - The username to authenticate with.
     * @param {string} authToken - The auth token.
     * @param {string} identityToken - The identity token.
     * @param {string} authSign - The auth signature.
     * @returns {Promise<IApisLogoutResponse} A promise resolving to the refreshed auth token response data.
     * @throws {Error} If the API token is invalid.
     */
    async logout(username: string, authToken: string, identityToken: string, authSign = ''): Promise<boolean> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.logoutUseCase.execute(username, authToken, identityToken, authSign, apiToken);
    }

    async forgetResendPin(payload: IForgetResendPinRequest): Promise<IForgetResendPinResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.forgetResendPinUseCase.execute(payload, apiToken);
    }

    async b2bForgotPin(apiKey: string, username: string, identityToken: string, authToken: string): Promise<IForgetPinResponse['data']> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.b2bForgetPinUseCase.execute(apiKey, username, identityToken, authToken, apiToken);
    }

    async verifyEmail(payload: IVerifyEmailRequest): Promise<IVerifyEmailResponse> {
        return await this.verifyEmailUseCase.execute(payload);
    }


}
